import React from 'react'

import { SimpleTable, TableSkeleton, type AddressCell, Alert } from '@/components/ui'
import { useApi, useDashboard, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin30Days } from '@/insights/common/formatUtils'
import genericLocationDrilldown from '@/insights/common/genericLocationDrilldown'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptionsWithCustom } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { celsiusToUser } from '@/utils/conversions'
import { downloadCsv } from '@/utils/csvExport'
import { formatDate, standardDateTimeFormat } from '@/utils/formatDate'

export const THERMO_KING_MAKE = 'Thermo King'
export const CARRIER_ADVANCED_MAKE = 'Carrier: Advanced'

interface ReeferFridgeDataHistoryParams extends InsightParametersType {
  companyId?: number
  assetId?: string
  date?: string
  from?: string
  to?: string
}

export interface ReeferFridgeDataSample {
  id: number
  companyId: number
  assetId: number
  assetName: string
  umdId: number
  sampleTime: Date
  fridgeMake: string | null
  numberOfZones: number | null
  location: string | null
  latitude: number | null
  longitude: number | null
  compressorDischargePressure: number | null
  compressorSuctionPressure: number | null
  compressorDischargeTemperature: number | null
  compressorSMVPosition: number | null
  evaporatorValvePosition: number | null
  evaporatorOutletPressure: number | null
  zone1EvaporatorOutletTemperature: number | null
  zone2EvaporatorOutletTemperature: number | null
  zone3EvaporatorOutletTemperature: number | null
  zone1DefrostTerminationTemperature: number | null
  zone2DefrostTerminationTemperature: number | null
  zone3DefrostTerminationTemperature: number | null
  engineRPM: number | null
  coolantTemperature: number | null
  batteryCurrent: number | null
  acPhase1Current: number | null
  acPhase2Current: number | null
  acPhase3Current: number | null
  airFlow: string | null
}

const toDecimal = (value: number | null | undefined, digits = 1): string =>
  value === null || value === undefined || !Number.isFinite(value) ? '' : value.toFixed(digits)

const toPlain = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value)

const thermoKingEtvPosition = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? '' : String(Math.round(value * 8))

type HeaderType = { key: string; title: string; size: number } | string

const ReeferFridgeDataHistory: InsightDefinition<ReeferFridgeDataHistoryParams, ReeferFridgeDataSample[]> = {
  active: true,
  slug: 'asset/reefer-fridge-data-history',
  category: 'asset',
  name: 'Reefer Fridge Data History',
  description: 'List detailed reefer fridge data history',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetId', control: 'AssetDropdownSingle', required: true, assetType: AssetTypeFilter.InTransit },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptionsWithCustom.map((d) => (d.value === 'today' ? { ...d, isDefault: true } : d)),
      },
      {
        id: 'customDate',
        label: 'Date',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (configuration: ReeferFridgeDataHistoryParams) => {
      if (!configuration?.companyId || !configuration.assetId) {
        return 'Company and Asset are required.'
      }

      if (configuration.date === 'custom') {
        if (!configuration.from || !configuration.to) {
          return 'Enter the custom date range'
        }
        const fromdate = new Date(configuration.from)
        const todate = new Date(configuration.to)
        if (fromdate > todate) {
          return 'Start date must be before end date'
        }
        const dateValidation30Days = validateDateRangeWithin30Days(fromdate.toISOString(), todate.toISOString())
        if (dateValidation30Days !== true) {
          return dateValidation30Days
        }
      }

      return true
    },
  },

  load: async (api: ApiService, parameters: ReeferFridgeDataHistoryParams) => {
    const path = `/insights/asset/reefer-fridge-data-history?${buildQuery(parameters)}`
    const response = await api.get<ReeferFridgeDataSample[]>({
      path,
      origin: 'ReeferFridgeDataHistory',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data?.length) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: ReeferFridgeDataHistoryParams, title: string) => {
    const path = '/insights/asset/reefer-fridge-data-history'
    await downloadCsv(api, path, parameters, title)
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }

    if (showSkeleton) {
      return <TableSkeleton columns={16} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display before displaying.
      // Framework will handle the other states like error and no data.
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }

    if (!data?.length) {
      return null
    }

    const parameters = configuration.parameters as ReeferFridgeDataHistoryParams
    const { fridgeMake, numberOfZones } = data[0]
    const zoneCount = numberOfZones ?? 0

    if (fridgeMake !== THERMO_KING_MAKE && fridgeMake !== CARRIER_ADVANCED_MAKE) {
      return (
        <Alert
          type="info"
          message={`Reefer fridge data is not available for this reefer make${fridgeMake ? ` (${fridgeMake})` : ''}.`}
        />
      )
    }

    const isThermoKing = fridgeMake === THERMO_KING_MAKE
    const headers: HeaderType[] = [{ key: 'Date', title: 'Date', size: 200 }, 'Location']

    if (isThermoKing) {
      headers.push(
        'Discharge Press(PSIG)',
        'Suction Press(PSIG)',
        'ETV position',
        'Engine RPM',
        'Engine Temp',
        'Battery Current(A)'
      )
      if (zoneCount > 0) headers.push('Evap Coil Temp')
      if (zoneCount > 1) headers.push('Evap Coil Temp 2')
      if (zoneCount > 2) headers.push('Evap Coil Temp 3')
    } else {
      headers.push(
        'Discharge Press(PSIG)',
        'Suction Press(PSIG)',
        'Evap Coil Temp',
        'Evap Outlet Press(PSIG)',
        'EVXV Position(%)',
        'SMV(%)',
        'Engine RPM',
        'Engine Temp',
        'Battery Current(A)',
        'AC Current 1(A)',
        'AC Current 2(A)'
      )
      if (zoneCount > 0) headers.push('Z1 DTT')
      if (zoneCount > 1) headers.push('Z2 DTT')
      if (zoneCount > 2) headers.push('Z3 DTT')
      headers.push('Discharge Temp', 'Air Flow')
    }

    const body = data.map((sample): Record<string, string | number | AddressCell> => {
      const sampleTime = new Date(sample.sampleTime)
      const row: Record<string, string | number | AddressCell> = {
        Date: formatDate(sampleTime, standardDateTimeFormat),
        Location: genericLocationDrilldown({
          configuration,
          address: sample.location ?? undefined,
          latitude: sample.latitude ?? undefined,
          longitude: sample.longitude ?? undefined,
          companyId: parameters.companyId,
          markerFields: {
            Asset: sample.assetName,
            Time: formatDate(sampleTime, standardDateTimeFormat),
          },
        }),
        'Discharge Press(PSIG)': toDecimal(sample.compressorDischargePressure),
        'Suction Press(PSIG)': toDecimal(sample.compressorSuctionPressure),
      }

      if (isThermoKing) {
        Object.assign(row, {
          'ETV position': thermoKingEtvPosition(sample.evaporatorValvePosition),
          'Engine RPM': toPlain(sample.engineRPM),
          'Engine Temp': celsiusToUser(sample.coolantTemperature, 1, units),
          'Battery Current(A)': toDecimal(sample.batteryCurrent),
        })
        if (zoneCount > 0) {
          row['Evap Coil Temp'] = celsiusToUser(sample.zone1EvaporatorOutletTemperature, 1, units)
        }
        if (zoneCount > 1) {
          row['Evap Coil Temp 2'] = celsiusToUser(sample.zone2EvaporatorOutletTemperature, 1, units)
        }
        if (zoneCount > 2) {
          row['Evap Coil Temp 3'] = celsiusToUser(sample.zone3EvaporatorOutletTemperature, 1, units)
        }
        return row
      }

      Object.assign(row, {
        'Evap Coil Temp': celsiusToUser(sample.zone1EvaporatorOutletTemperature, 1, units),
        'Evap Outlet Press(PSIG)': toDecimal(sample.evaporatorOutletPressure),
        'EVXV Position(%)': toPlain(sample.evaporatorValvePosition),
        'SMV(%)': toPlain(sample.compressorSMVPosition),
        'Engine RPM': toPlain(sample.engineRPM),
        'Engine Temp': celsiusToUser(sample.coolantTemperature, 1, units),
        'Battery Current(A)': toDecimal(sample.batteryCurrent),
        'AC Current 1(A)': toDecimal(sample.acPhase1Current),
        'AC Current 2(A)': toDecimal(sample.acPhase2Current),
      })
      if (zoneCount > 0) {
        row['Z1 DTT'] = celsiusToUser(sample.zone1DefrostTerminationTemperature, 1, units)
      }
      if (zoneCount > 1) {
        row['Z2 DTT'] = celsiusToUser(sample.zone2DefrostTerminationTemperature, 1, units)
      }
      if (zoneCount > 2) {
        row['Z3 DTT'] = celsiusToUser(sample.zone3DefrostTerminationTemperature, 1, units)
      }
      row['Discharge Temp'] = celsiusToUser(sample.compressorDischargeTemperature, 1, units)
      row['Air Flow'] = sample.airFlow ?? ''
      return row
    })

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} cellClassNames={{ Date: 'text-nowrap' }} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
    const params = configuration.parameters as ReeferFridgeDataHistoryParams
    React.useEffect(() => {
      if (!api) {
        return
      }
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/fresh/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false`,
            origin: 'ReeferFridgeDataHistory#caption',
          })
          if (response.data?.length) {
            setAsset(response.data[0])
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    fields['Temperature Units'] = units === 'Imperial' ? '°F' : '°C'
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default ReeferFridgeDataHistory
